import { SerializedError, miniSerializeError } from "@reduxjs/toolkit";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import {
    IAddress,
    IBillingAddress,
    IShippingAddress,
} from "../../models/address";
import { IBasket } from "../../models/catalogue";
import { FinancingPlan } from "../../models/financing";
import {
    filterFinancingPlans,
    sortFinancingPlans,
} from "../../utils/financing";
import { objectKeys } from "../../utils/functional";
import { getPageSetting } from "../../utils/settings";
import { PRIMARY_PAYMENT_METHOD_KEY } from "./constants";
import { IFinancingPayment, IReduxState } from "./reducers.interfaces";

export type IErrorMsgTemplateContext = {
    basketID?: string;
    orderID?: string;
};

const _getAddress = (type: "shipping" | "billing", state: IReduxState) => {
    const prefix = `${type}_`;
    const address = objectKeys(state.form)
        .filter(
            (key): key is keyof IShippingAddress | keyof IBillingAddress => {
                return key.indexOf(prefix) === 0;
            },
        )
        .reduce<Partial<IAddress>>((memo, key) => {
            const outKey = key.replace(prefix, "") as keyof IAddress;
            return {
                ...memo,
                [outKey]: state.form[key],
            };
        }, {});
    return address as IAddress;
};

export const getShippingAddress = (state: IReduxState) => {
    return _getAddress("shipping", state);
};

export const getBillingAddress = (state: IReduxState) => {
    return _getAddress("billing", state);
};

export const getCountryCode = (url: string) => {
    // TODO: make this less awful
    // TODO(i18n): "countries" is a hardcoded URL segment that may need localization if URLs are translated
    const segments = url.split("/");
    return segments[segments.indexOf("countries") + 1];
};

/**
 * The financing plans checkout offers for a basket. They come inside the basket
 * response, chosen by the same offers that priced it, so reloading the basket
 * is what refreshes them.
 */
export const getBasketFinancingPlans = (
    basket: IBasket | null,
): FinancingPlan[] => {
    return sortFinancingPlans(
        filterFinancingPlans(basket?.financing_plans ?? []),
    );
};

const MAX_OPAQUE_BODY_LENGTH = 500;

export type ISerializedCheckoutError = SerializedError & {
    response?: Readonly<{
        body?: unknown;
        status?: number;
    }>;
};

export const serializeCheckoutError = (
    err: unknown,
): ISerializedCheckoutError => {
    const serialized: ISerializedCheckoutError = miniSerializeError(err);
    const response =
        err && typeof err === "object"
            ? (err as { response?: unknown }).response
            : undefined;
    if (response && typeof response === "object" && "body" in response) {
        const { body, status } = response as {
            body?: unknown;
            status?: unknown;
        };
        let safeBody: unknown;
        if (body && typeof body === "object") {
            safeBody = body;
        } else if (typeof body === "string") {
            safeBody = body.slice(0, MAX_OPAQUE_BODY_LENGTH);
        }
        return {
            ...serialized,
            response: {
                body: safeBody,
                status: typeof status === "number" ? status : undefined,
            },
        };
    }
    return serialized;
};

export const renderErrorMessageTemplate = (
    template: string,
    data: IErrorMsgTemplateContext,
) => {
    // Guard against empty template string
    if (!template) {
        return "";
    }
    let error = template;

    // Render old style error message templates (for backwards compatibility)
    // Replace the BASKET_ID placeholder if it exists
    if (data.basketID) {
        error = error.replace("BASKET_ID", data.basketID);
    }
    // Replace the ORDER_ID placeholder if it exists
    if (data.orderID) {
        error = error.replace("ORDER_ID", data.orderID);
    }
    const websiteSupportNumber = getPageSetting(
        "support_website_phone_number_display",
    );
    if (websiteSupportNumber) {
        error = error.replace("WEBSITE_SUPPORT_NUMBER", websiteSupportNumber);
    }
    const websiteSupportContactHours = getPageSetting("support_contact_hours");
    if (websiteSupportContactHours) {
        error = error.replace(
            "WEBSITE_SUPPORT_CONTACT_HOURS",
            websiteSupportContactHours,
        );
    }

    // Render new-style error message templates
    return interpolate(error, {
        basketID: data.basketID || "",
        orderID: data.orderID || "",
        websiteSupportNumber: websiteSupportNumber || "",
        websiteSupportContactHours: websiteSupportContactHours || "",
    });
};

export const getFinancingData = (
    props: IReduxState,
): IFinancingPayment | null => {
    const data = props.form.payment_methods[PRIMARY_PAYMENT_METHOD_KEY];
    if (data.method_type === "financing") {
        return data;
    }
    return null;
};

export const isNewFinancingAccount = (props: IReduxState): boolean => {
    const data = getFinancingData(props);
    if (data) {
        if (data.new_financing_account) {
            return true;
        }
    }
    return false;
};

export const isFortivaAcctNum = (value: string) => {
    return value.startsWith("7656") || value.startsWith("2000");
};
